// Phase 24 M4 — Cloudflare Turnstile widget wrapper.
//
// Loads the Cloudflare script lazily, mounts the widget, and emits the
// token via `onToken` so the AuthModal can attach it to the register
// payload. When `siteKey` is empty the widget is hidden — useful for
// dev / CI / tests where the backend Turnstile verifier returns ok=true.

import { useEffect, useRef } from 'react';

interface TurnstileWindow extends Window {
  turnstile?: {
    render: (
      el: HTMLElement,
      opts: {
        sitekey: string;
        callback: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
      },
    ) => string;
    remove: (id: string) => void;
  };
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function ensureScript(): Promise<void> {
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(s);
  });
}

export interface TurnstileProps {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: () => void;
}

export function Turnstile({ siteKey, onToken, onError }: TurnstileProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  // Latest-callback refs. The parent typically passes inline arrow
  // functions to `onToken` / `onError`, so they break referential
  // equality on every render. If we listed them in the useEffect
  // deps, each parent re-render would unmount + remount the widget —
  // and since `onToken` itself triggers a parent re-render (it
  // stores the token in state), the mount/unmount cycle becomes an
  // infinite loop. Symptom: captcha keeps verifying, screen jumps,
  // form never advances. Reading the callbacks via refs decouples
  // them from the mount lifecycle.
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
  }, [onToken, onError]);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !ref.current) return;
        const w = window as TurnstileWindow;
        if (!w.turnstile) return;
        widgetId.current = w.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'error-callback': () => onErrorRef.current?.(),
          'expired-callback': () => onErrorRef.current?.(),
        });
      })
      .catch(() => onErrorRef.current?.());
    return () => {
      cancelled = true;
      const w = window as TurnstileWindow;
      if (widgetId.current && w.turnstile) {
        w.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} aria-label="cloudflare-turnstile" />;
}
